import React, {useState} from "react";
import {KulturNavSearch} from "../kulturnav/KulturNavSearch";
import {damsSearch} from "../app/damsFetch";
import {
    concatWithExisting,
    excludeThoseAlreadyListedAsKulturnav,
    fieldValuesWithCollectionIds,
    inputWithCollectionIds,
    sourceToUniqueId,
} from "../metadata/inputMapper";
import {FormNewPlace} from "./FormNewPlace";
import {useAddPlace} from "./useAddPlace";
import {ModalCreateResource} from "../metadata/ModalCreateResource";
import {ModalCreateResourceHeader} from "../metadata/ModalCreateResourceHeader";
import {useField, useFormikContext} from "formik";
import {useDocumentState, useDocumentTranslation,} from "../documents/documentContext";
import decamelizeKeysDeep from "decamelize-keys-deep";
import {useAuthsState} from "../auths/authsContext";
import PlaceIcon from "@mui/icons-material/Place";
import {mapNotMissing} from "../inpututils";
import {clientLog} from "../clientLog";
import {kulturnavApiCoreFetch} from "../app/kulturnavFetch";
import {apiCoreObjectToDAMSInput} from "../utility";

/**
 * Renders an input component for selecting places.
 *
 * @param {string} formikKey - The key for the Formik field.
 * @param {string} label - The label for the input.
 * @param {string} createLabel - The label for the create button.
 * @param {boolean} [disabled=false] - Whether the input is disabled.
 * @param {string} [size="large"] - The size of the input.
 * @param {string|null} [projectCollectionId=null] - The project collection ID.
 * @return {JSX.Element} The rendered input component.
 */
export const InputPlaces = ({
                                formikKey,
                                label,
                                createLabel,
                                disabled = false,
                                size = null,
                                projectCollectionId = null,
                            }) => {
    const t = useDocumentTranslation();
    const [field, meta] = useField(formikKey);
    const {setFieldValue} = useFormikContext();
    const [newPlace, setNewPlace] = useState(null);
    const postPlaces = useAddPlace();
    const {collectionIds} = useDocumentState();
    const {museumCollections} = useAuthsState();

    // NOTE: When editing data for a project, only one collectionID is selected, and is sent directly to this component.
    const currentCollectionIds = collectionIds.length === 0 ? [projectCollectionId] : collectionIds;

    const museums = museumCollections
        .filter((museumCollection) =>
            currentCollectionIds.includes(museumCollection.collectionId)
        )
        .map((museumCollection) => museumCollection.museumId);


    const kulturNavFetchData = async (params) => {
        if (params.length < 3) {
            return;
        }

        try {
            const excludeDatasets = window._env_.REACT_APP_PLACES_EXCLUDE_DATASETS;
            const excludeDatasetsClause = excludeDatasets.map(ed => `entity.dataset:!${ed}`).join(',');
            const removeDeprecatedKey = 'superconcept.status:853a2d84-5b2b-11e2-bcfd-0800200c9a66';

            const url = `entityType:Place,compoundName:${params},${removeDeprecatedKey},${excludeDatasetsClause}?properties=entity.dataset,entity.fullCaption&displayValue=true`;
            const data = await kulturnavApiCoreFetch(url);
            const {entities} = data;

            const mapped = [...entities]
                .map(e => {
                    return apiCoreObjectToDAMSInput(e);
                })
                .filter(m => Object.keys(m).length > 0);

            return Promise.resolve(mapped);
        } catch (error) {
            clientLog('error', error, 'inputplaces');
            return Promise.resolve([]);
        }
    };

    const placesSearchHandler = (params, callback) => {
        const searchParams = new URLSearchParams(
            decamelizeKeysDeep({
                q: `_title_:${params}`,
                fl: "title,id",
                sort: "title asc",
                expand: true,
                documentType: `("place")`,
                museums: museums.join(","),
            })
        );

        Promise.all([
            kulturNavFetchData(params),
            damsSearch(searchParams).then((json) =>
                Promise.resolve(inputWithCollectionIds(json['models']))
            ),
        ]).then(([kulturnavResponse, damsResponse]) => {
            const damsLookup = typeof (damsResponse) !== 'undefined' ? sourceToUniqueId(damsResponse) : {};
            const kulturnavMapped = concatWithExisting((typeof (kulturnavResponse) !== 'undefined' ? kulturnavResponse : []), damsLookup);
            callback([
                ...kulturnavMapped,
                ...excludeThoseAlreadyListedAsKulturnav(damsResponse, kulturnavMapped),
            ]);
        }).catch(error => {
            clientLog('error', error, 'placessearchhandler');
            return [];
        });
    };

    const onAdd = (places) => {
        setFieldValue(field.name, [...field.value, ...places]).then();
    };

    const onChange = (places) => {
        setFieldValue(field.name, places).then();
    };

    const postMissing = async (value) => {
        const places = currentCollectionIds
            .filter((collectionId) => !value.collectionId.includes(collectionId))
            .filter(() => !value.uniqueId && !value.id)
            .map((collectionId) => ({
                ...value,
                collectionId: collectionId
            }));
        return await postPlaces({places: places});
    };

    const changeHandler = async (values) => {
        const addingPlace = values.find(value => value.create);
        const createExternalPlace = values.find(place => !place.uniqueId);

        if (addingPlace) {
            setNewPlace(addingPlace);
        } else if (createExternalPlace) {
            createExternalPlace['content']['nameAndTitle'] = createExternalPlace.title;
            const newPlaces = currentCollectionIds.map(collectionId => ({
                ...createExternalPlace,
                collectionId: collectionId
            }));
            const savedPlaces = await postPlaces({places: newPlaces})
            onAdd(savedPlaces);
        } else {
            const existingPlaces = values.map(v => mapNotMissing(currentCollectionIds, v, 'place')).flat();
            Promise.all(values.map(postMissing)).then((newPlaces) =>
                onChange([...existingPlaces, ...newPlaces.flat()])
            );
        }
    };

    const clearHandler = () => {
        onChange([]);
    };

    const getValue = () => {
        if (field.value) {
            return fieldValuesWithCollectionIds(field.value);
        } else {
            return [];
        }
    };

    if (undefined === meta.initialValue || !currentCollectionIds.length) {
        return null;
    }

    return (
        <>
            <KulturNavSearch
                id={"search-knav-places"}
                searchHandler={placesSearchHandler}
                onChange={changeHandler}
                onClear={clearHandler}
                disabled={disabled}
                inputLabel={label}
                Icon={PlaceIcon}
                value={getValue()}
                size={size}
                helperText={t("placesHelp", "Som omtales/er avbildet")}
            />
            <ModalCreateResource
                open={null !== newPlace}
                onHide={() => setNewPlace(null)}
            >
                <ModalCreateResourceHeader title={createLabel}/>
                <FormNewPlace
                    place={newPlace}
                    onClose={() => setNewPlace(null)}
                    addHandler={onAdd}
                    collectionIds={currentCollectionIds}
                />
            </ModalCreateResource>
        </>
    );
};
